import React from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../redux/store";

// Wrappers & Auth
import PublicWrapper from "../wrapper/PublicWrapper";
import AppWrapper from "../wrapper/AppWrapper";
import ProtectedRoute from "../auth/ProtectedRoute";

// Public Pages
import HomePage from "../pages/HomePage";
import LoginPage from "../pages/LoginPage";
import ForgotPasswordPage from "../pages/ForgotPasswordPage";
import UnauthorizedPage from "../pages/UnauthorizedPage";
import NotFoundPage from "../pages/NotFoundPage";
import ResetPasswordPage from "../pages/ResetPasswordPage";
import EmailVerificationPage from "../pages/EmailVerificationPage";

// Standalone Pages
import ProfilePage from "../pages/ProfilePage";
import SettingsPage from "../pages/SettingsPage";
import CompanyPage from "../pages/CompanyPage";

const AppRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useAppSelector((state) => state.auth);

  React.useEffect(() => {
    if (userInfo) {
      // 1. Exact matches for static auth pages
      const staticAuthRoutes = ["/", "/login", "/forgot-password"];

      // 2. Dynamic matches for token-based auth pages (User might click an email link while already logged in)
      // Usually, if they are already logged in, you STILL want to redirect them to the dashboard
      // because they don't need to reset their password via email link if they have access.
      const isDynamicAuthRoute =
        location.pathname.startsWith("/password-reset/") ||
        location.pathname.startsWith("/email-verify/");

      if (staticAuthRoutes.includes(location.pathname) || isDynamicAuthRoute) {
        // Redirect to the intended page or the dashboard
        const from = location.state?.from?.pathname || "/dashboard";
        navigate(from, { replace: true });
      }
    }
  }, [userInfo, location, navigate]);

  return (
    <Routes>
      {/* 1. PUBLIC ROUTES */}
      <Route element={<PublicWrapper />}>
        <Route index element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/password-reset/:token" element={<ResetPasswordPage />} />
        <Route
          path="/email-verify/:token"
          element={<EmailVerificationPage />}
        />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
      </Route>

      {/* 2. PROTECTED APP ROUTES (AppWrapper acts as the shell) */}
      <Route
        element={
          <ProtectedRoute>
            <AppWrapper />
          </ProtectedRoute>
        }
      >
        {/* A. Standalone Pages (Rendered via <Outlet /> in AppWrapper) */}
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/company" element={<CompanyPage />} />
        <Route path="/settings" element={<SettingsPage />} />

        {/* B. The Workspace Catch-All (For /dashboard, /users, /items, etc.) */}
        {/* We just need this so React Router doesn't throw a 404. AppWrapper handles the rendering. */}
        <Route path="/*" element={<></>} />
      </Route>

      {/* 3. 404 Route */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
